/**
 * Which of an MC item's published shifts still need a cover ask.
 *
 * Backlog #99 (checklist 13.23). "Ask the team" pre-checked EVERY shift the
 * MC touched, including seats the ladder had already filled: with only
 * `55 PM` short, the picker still offered — and sent — an ask for `55 AM`
 * too, and once the PM ask was accepted the redundant AM ask stayed out with
 * the team. A cover ask for a covered seat is noise in five people's feeds.
 *
 * The attention payload already carries the answer in two grammars that have
 * to be joined:
 *
 *   * `still_short` — the LADDER's labels for the seats this MC vacated that
 *     are still short (`"55 PM"`, `"56 FULL_DAY"`, `"NIGHT on-call"`).
 *   * `affected_assignments[].role` — the PERSISTED role token of each of
 *     the member's published rows (`"STATION_55_PM"`, `"ONCALL_NIGHT"`).
 *
 * We join by BUILDING the role tokens a label implies rather than by parsing
 * a role token apart: a station name may contain `_` (`"WARD_55"`), so
 * `STATION_WARD_55_PM` cannot be split back reliably, while the label
 * `"WARD_55 PM"` reconstructs to exactly one token. A `FULL_DAY` label
 * expands to the AM + PM tokens the roster actually persists (the solver
 * unpacks a full-day ward into two rows — `backend/.../shortfalls.py`
 * `_model_sessions`).
 *
 * When `still_short` is empty the ladder cannot speak about these roles at
 * all (an unsolved cycle, a ward off the config). "Can't say" must not read
 * as "already filled", so we fall back to selecting everything — exactly
 * what the picker did before.
 */

/** Every persisted role token the ladder label `label` can refer to. */
export function roleTokensForShortLabel(label: string): string[] {
  const trimmed = label.trim();
  if (!trimmed) return [];
  const ONCALL_SUFFIX = " on-call";
  if (trimmed.endsWith(ONCALL_SUFFIX)) {
    const key = trimmed.slice(0, -ONCALL_SUFFIX.length);
    return key ? [`ONCALL_${key}`] : [];
  }
  const cut = trimmed.lastIndexOf(" ");
  if (cut <= 0) return [];
  const station = trimmed.slice(0, cut);
  const session = trimmed.slice(cut + 1).toUpperCase();
  if (session === "FULL_DAY" || session === "FULLDAY" || session === "FD") {
    return [`STATION_${station}_AM`, `STATION_${station}_PM`];
  }
  return [`STATION_${station}_${session}`];
}

/** The role tokens `still_short` names, as a set. Empty = "can't say". */
export function shortRoleTokens(stillShort: readonly string[] | undefined): Set<string> {
  return new Set((stillShort ?? []).flatMap(roleTokensForShortLabel));
}

/** A ladder cell's label for one ward, spelled the way the backend spells
 *  `still_short` (`shortfalls._label`): `"55 AM"`, `"56 FULL_DAY"`,
 *  `"NIGHT on-call"`. */
function ladderLabel(station: string, session: string): string {
  const s = session.trim().toUpperCase();
  return s === "ONCALL" ? `${station} on-call` : `${station} ${s}`;
}

export interface LadderCellSeats {
  station: string;
  session: string;
  kind?: string | null;
  stations?: readonly string[] | null;
}

/**
 * Every persisted role token a ladder cell's seats are held under — the
 * join the "Ask the team" button on an empty cell uses to find the sick
 * member's seat behind the gap. A ward's cell is its own ward in that
 * session; a pooled floor's cell is EVERY ward of the group in that session
 * (the member whose absence opened the pooled gap may have sat on any of
 * them) — the same mapping the backend's `ladder_gaps_by_day` builds.
 */
export function cellRoleTokens(cell: LadderCellSeats): Set<string> {
  const wards =
    cell.kind === "floor" && (cell.stations ?? []).length > 0
      ? (cell.stations ?? [])
      : [cell.station];
  return new Set(wards.flatMap((ward) => roleTokensForShortLabel(ladderLabel(ward, cell.session))));
}

export interface CoverAskSeat {
  assignment_id: string;
  role: string;
}

/**
 * The assignment ids to pre-check (and the only ones an ask may be sent
 * for). Falls back to every seat when the ladder can't say what is short.
 */
export function coverAskDefaultSelection(
  seats: readonly CoverAskSeat[],
  stillShort: readonly string[] | undefined,
): string[] {
  const short = shortRoleTokens(stillShort);
  if (short.size === 0) return seats.map((s) => s.assignment_id);
  const stillOpen = seats.filter((s) => short.has(s.role)).map((s) => s.assignment_id);
  // Every seat reads as covered while the ladder DID speak: don't hand the
  // lead an empty picker with a dead Send — offer the lot and let them judge.
  return stillOpen.length > 0 ? stillOpen : seats.map((s) => s.assignment_id);
}

/** Is this seat one the ladder still calls short? `false` only when the
 *  ladder could speak and did not name it. */
export function coverAskSeatIsShort(
  seat: CoverAskSeat,
  stillShort: readonly string[] | undefined,
): boolean {
  const short = shortRoleTokens(stillShort);
  if (short.size === 0) return true;
  return short.has(seat.role);
}
